import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import AgentFunctions from "../../API/AgentFunctions";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../Router";
import FunctionSettingAI from "../Settings/Components/Functions/FunctionSettingsAI";
import ToolTestPanel from "../Settings/Components/SubComponents/ToolTestPanel";
import AgentBuilderSidebar from "../Agents/Components/AgentBuilderSidebar";

const HASH_ROUTED_TABS = ["configure", "test"];
const HASH_ROUTED_SECTIONS = [
  "profile",
  "endpoint",
  "auth",
  "parameters",
  "behaviour",
  "usage",
  "versions",
];

const DEFAULT_TOOL_VALUES = {
  name: "",
  description: "",
  scope: null,
  parameters: {
    type: "object",
    properties: {},
    required: [],
  },
  requestType: "GET",
  authorisationType: "NONE",
  url: "",
  model: "speed",
  headers: {},
  staticArgs: {},
  isDatabaseScript: false,
  paramsAsPath: false,
  graphqlQuery: "",
  graphqlMutation: "",
  graphqlOperationName: "",
};

const parseMaybeJson = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  }
  return value;
};

const ToolBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const toolId = params?.toolId;
  const isEditMode = !!toolId && toolId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace, getWorkspaceUsers } = useWorkspaces();

  const [activeTab, setActiveTab] = useState("configure");
  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isFunctionsLoading, setIsFunctionsLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedTool = useMemo(
    () => state?.agentsFunctions?.find((u) => u?.id === toolId),
    [state?.agentsFunctions, toolId],
  );

  const loadAgentsFunctions = async (workspaceID) => {
    try {
      setIsFunctionsLoading(true);
      const functionsData = await AgentFunctions.getAgentFunctionsByWorkspaceID(
        {
          workspaceID,
        },
      );
      let functions = functionsData?.data?.functionsByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true)
              return {
                ...u,
                parameters: parseMaybeJson(u?.parameters, {}),
                headers: parseMaybeJson(u?.headers, {}),
                staticArgs: parseMaybeJson(u?.staticArgs, {}),
                requestType: u?.requestType?.toUpperCase(),
              };
          })
          ?.filter((u) => u != null) || [];
      functions = functions.sort((a, b) => a.name.localeCompare(b.name));
      dispatch("SET_AGENTS_FUNCTIONS", functions || []);
    } catch (error) {
      AppToasty.show(t("failedToLoadFunctionsCaption"), {
        type: "danger",
      });
    } finally {
      setIsFunctionsLoading(false);
    }
  };

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const functions = state?.agentsFunctions || [];
    const belongsToWorkspace = functions.some((u) => u?.workspaceID === wsId);
    if (functions.length === 0 || !belongsToWorkspace) {
      loadAgentsFunctions(wsId);
    } else {
      setIsFunctionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!toolId) {
      history.replace(`/workspaces/${workspaceId}/settings/functions`);
    }
  }, [toolId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (toolId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    const toolConfig = history.location?.state?.toolConfig || null;
    setInitialMapValues(
      toolConfig
        ? {
            ...toolConfig,
            authorisationType: toolConfig?.authorisationType?.toUpperCase(),
            parameters:
              toolConfig?.parameters != null || !toolConfig?.isMCPServer
                ? toolConfig?.parameters
                : { type: "object", properties: {}, required: [] },
          }
        : DEFAULT_TOOL_VALUES,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, isEditMode]);

  const prepareEditValues = useCallback(
    (tool) => {
      const enhancedUsers = tool?.usersToHandOffTo
        ?.map((id) => {
          const u = getWorkspaceUsers()?.find((x) => x?.id === id);
          if (!u) return null;
          return {
            ...u,
            label: u?.username || u?.user_email,
            value: u?.id,
            backgroundColor: AppUtilities.stringToHslColor(
              u?.username || u?.user_email,
              71,
            ),
          };
        })
        ?.filter((u) => u);
      return {
        ...tool,
        parameters: parseMaybeJson(tool?.parameters, {}),
        headers: parseMaybeJson(tool?.headers, {}),
        staticArgs: parseMaybeJson(tool?.staticArgs, {}),
        authorisationType: tool?.authorisationType?.toUpperCase(),
        requestType: tool?.requestType?.toUpperCase(),
        usersToHandOffTo: enhancedUsers,
      };
    },
    [getWorkspaceUsers],
  );

  useEffect(() => {
    if (!isEditMode) return;
    if (isFunctionsLoading) return;
    if (editInitRef.current === toolId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const tool = (state?.agentsFunctions || []).find((u) => u?.id === toolId);
    if (!tool) {
      const listIsStale = state?.agentsFunctions?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId,
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/functions`);
      return;
    }
    editInitRef.current = toolId;
    setInitialMapValues(prepareEditValues(tool));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toolId,
    isEditMode,
    isFunctionsLoading,
    state?.agentsFunctions,
    getActiveWorkspace()?.id,
  ]);

  const flags = initialMapValues || {};
  const isScriptFunction = flags?.isChatFn || flags?.isAgentSkill;
  const isNotBase = getActiveWorkspace()?.subscriptionType !== "base";

  const isSectionVisible = (sectionId) => {
    switch (sectionId) {
      case "profile":
      case "endpoint":
        return true;
      case "auth":
        return (
          !isScriptFunction &&
          !flags?.isDatabaseScript &&
          !flags?.isCodeTemplate &&
          !flags?.isHtmlFn
        );
      case "parameters":
        return !flags?.isHtmlFn && !isScriptFunction && !flags?.isMCPServer;
      case "behaviour":
        return (
          !flags?.isMCPServer &&
          !isScriptFunction &&
          (isNotBase || !flags?.isHtmlFn)
        );
      case "usage":
      case "versions":
        return isEditMode;
      default:
        return true;
    }
  };

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("tools.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("tools.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.agent,
          },
          {
            id: "endpoint",
            title: t("tools.builderSectionEndpoint", {
              defaultValue: "Connection",
            }),
            icon: AppAssets.icons.network,
          },
          {
            id: "auth",
            title: t("tools.builderSectionAuth", {
              defaultValue: "Authorisation",
            }),
            icon: AppAssets.icons.credentials,
          },
          {
            id: "parameters",
            title: t("tools.builderSectionParameters", {
              defaultValue: "Parameters",
            }),
            icon: AppAssets.icons.experiment,
          },
          {
            id: "behaviour",
            title: t("tools.builderSectionBehaviour", {
              defaultValue: "Behaviour",
            }),
            icon: AppAssets.icons.bolt,
          },
        ],
      },
      {
        id: "system",
        label: t("tools.builderGroupSystem", { defaultValue: "System" }),
        items: [
          {
            id: "usage",
            title: t("tools.builderSectionUsage", { defaultValue: "Usage" }),
            icon: AppAssets.icons.taskAgent,
          },
          {
            id: "versions",
            title: t("tools.builderSectionVersions", {
              defaultValue: "Versions",
            }),
            icon: AppAssets.icons.history,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    isNotBase,
    flags?.isChatFn,
    flags?.isAgentSkill,
    flags?.isMCPServer,
    flags?.isHtmlFn,
    flags?.isDatabaseScript,
    flags?.isCodeTemplate,
  ]);

  useEffect(() => {
    if (activeTab === "configure" && !isSectionVisible(activeSection)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, initialMapValues]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_TABS.includes(raw)) {
        setActiveTab(raw);
        return;
      }
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveTab("configure");
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/tools/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const builderActiveSection = activeTab === "configure" ? activeSection : null;

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    console.log("[TOOL_SAVE] validation error sections:", errorSections);
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveTab("configure");
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("tools.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeTab === "test" ? "test" : builderActiveSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderActiveSection, activeTab]);

  // In edit mode, wait until the loaded values match the route id — otherwise
  // the form mounts with stale create-mode values and the dirty baseline /
  // normalization cycle runs twice, falsely flagging the form as changed.
  const isFormReady =
    initialMapValues != null &&
    !isFunctionsLoading &&
    (!isEditMode || initialMapValues?.id === toolId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [builderActiveSection]);

  const handleFunctionCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("functionCreationSuccessCaption"), {
        type: "success",
      });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/tools/${savedId}`);
      }
    } else {
      AppToasty.show(t("functionCreationFailedCaption"), {
        type: "danger",
      });
    }
  };

  const handleSaveClick = () => {
    const formik = formRef?.current;

    if (!formik) {
      console.warn("[TOOL_SAVE] formRef not mounted — form is not rendered");
      return;
    }
    try {
      const promise = formik.submitForm();
      if (promise && typeof promise.then === "function") {
        promise.then(() => {
          console.log("[TOOL_SAVE] after submitForm", {
            submitCount: formik.submitCount,
            isValid: formik.isValid,
            isSubmitting: formik.isSubmitting,
            errors: formik.errors,
            touchedKeys: Object.keys(formik.touched || {}),
          });
        });
      }
    } catch (e) {
      console.error("[TOOL_SAVE] submitForm threw synchronously", e);
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={handleSaveClick}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const tabs = [
    {
      id: "configure",
      label: t("tools.builderConfigureTab", { defaultValue: "Configure" }),
      icon: AppAssets.icons.tools,
    },
    {
      id: "test",
      label: t("tools.builderTestTab", { defaultValue: "Test" }),
      icon: AppAssets.icons.launch,
    },
  ];

  const renderTabButton = (tab) => {
    const isActive = activeTab === tab.id;
    return (
      <TouchableOpacity
        key={tab.id}
        onPress={() => setActiveTab(tab.id)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: 7,
          paddingHorizontal: 16,
          borderRadius: 20,
          backgroundColor: isActive
            ? currentTheme?.theme?.backgroundColor
            : currentTheme?.theme?.transparent,
          borderWidth: 1,
          borderColor: isActive
            ? currentTheme?.theme?.lightGrey
            : currentTheme?.theme?.transparent,
        }}
      >
        {tab.icon && (
          <AppIcon
            icon={tab.icon}
            size={13}
            color={
              isActive
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.darkGrey
            }
            style={{ marginRight: 7 }}
          />
        )}
        <AppText
          style={{ fontSize: 13 }}
          fontWeight={isActive ? "600" : "normal"}
          color={
            isActive
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.darkGrey
          }
        >
          {tab.label}
        </AppText>
      </TouchableOpacity>
    );
  };

  const renderSaveFirstHint = () => (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <AppIcon
        icon={AppAssets.icons.tools}
        size={26}
        color={currentTheme?.theme?.mediumGrey}
      />
      <AppText
        style={{ marginTop: 12, textAlign: "center" }}
        color={currentTheme?.theme?.mediumGrey}
      >
        {t("tools.builderSaveFirst", {
          defaultValue: "Create the tool first to unlock this section",
        })}
      </AppText>
    </View>
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingFunctions")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        {activeTab === "configure" && (
          <AgentBuilderSidebar
            groups={sectionGroups}
            activeSection={activeSection}
            onSelect={setActiveSection}
            errorSections={errorSections}
          />
        )}
        <ScrollView
          ref={scrollRef}
          style={{
            flex: 1,
            minWidth: 0,
            display: activeTab === "test" ? "none" : "flex",
          }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <FunctionSettingAI
            key={toolId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? toolId : undefined}
            settingType="function"
            onFunctionCreated={handleFunctionCreated}
            onDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/functions`)
            }
            setShowFunctionModal={() => {}}
            isBuilderLayout
            builderActiveSection={builderActiveSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
        <View
          style={{
            flex: 1,
            minWidth: 0,
            display: activeTab === "test" ? "flex" : "none",
          }}
        >
          {isEditMode ? (
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flexGrow: 1 }}
              showsVerticalScrollIndicator
            >
              <ToolTestPanel
                key={toolId}
                mode="tool"
                id={toolId}
                token={getActiveWorkspace()?.workspaceToken}
                parameters={initialMapValues?.parameters}
                staticArgs={initialMapValues?.staticArgs}
              />
            </ScrollView>
          ) : (
            renderSaveFirstHint()
          )}
        </View>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/functions`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.tools")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedTool?.name || ""
            : history.location?.state?.toolConfig?.name ||
              t("tools.builderNewTool", { defaultValue: "New tool" })}
        </AppText>
        <View
          style={{
            flex: 1,
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: 24,
              padding: 4,
              gap: 2,
            }}
          >
            {tabs.map(renderTabButton)}
          </View>
        </View>
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("tools.unsavedChangesSubCaption", {
          defaultValue:
            "This tool has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default ToolBuilderScreenContainer;
